import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listEvents } from '@/lib/events-api';
import { Calendar, Camera, Image, Grid3X3, Lock, MapPin, Clock, CheckCircle, Users, Heart, Check, Feather, BookOpen, Plus } from 'lucide-react';

function lockedReasonFor(status) {
  if (status === 'HOST_PENDING') return 'Awaiting host approval';
  if (status === 'SUSPENDED') return 'Uploads suspended';
  if (status === 'REMOVED') return 'Access removed';
  return 'Verify to unlock';
}

function handleUploadTilePress(navigate, eventId, status, canUpload, mode) {
  if (canUpload) { navigate(`/contrib-upload?event_id=${eventId}&mode=${mode}`); return; }
  if (status === 'HOST_PENDING') { navigate(`/host-approval-required?event_id=${eventId}`); return; }
  if (status === 'SUSPENDED' || status === 'REMOVED') {
    const title = status === 'SUSPENDED' ? 'Uploads suspended' : 'Access removed';
    const message = status === 'SUSPENDED'
      ? "The host has paused your uploads. Contact the host if you think this is a mistake."
      : "You've been removed from this event. Contact the host if you think this is a mistake.";
    window.alert(`${title}\n\n${message}`);
    return;
  }
  navigate(`/verify-attendance?event_id=${eventId}`);
}

export default function ContribEventPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready } = useRequireAuth();
  const [attending, setAttending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await listEvents();
      setAttending(body.attending || []);
    } catch { /* retry on focus */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="contrib-event-screen">
      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-1" data-testid="contrib-event-title">Your events</h1>
      <p className="text-sm text-[#5B6472] mb-6" data-testid="contrib-event-welcome">
        Hi {user?.first_name}. Everything you contribute stays private until the host publishes a Lookbook.
      </p>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="contrib-event-loading">
          <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : attending.length === 0 ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6" data-testid="contrib-no-events">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-[#146BFF] mt-0.5" />
            <div>
              <p className="font-bold text-[#0B1B46]">No events yet</p>
              <p className="text-sm text-[#5B6472] mt-1">Enter an Event ID or open the invite link the host sent you.</p>
            </div>
          </div>
          <button onClick={() => navigate('/join')} className="w-full h-10 mt-4 bg-[#146BFF] text-white font-semibold rounded-lg hover:bg-[#0F55CC] transition-colors" data-testid="contrib-join-btn">Join an Event</button>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {attending.map(({ event, membership }) => (
            <div key={event.id} className="flex flex-col gap-3">
              {/* Hero card */}
              <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid={`contrib-attending-${event.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF]">YOU'RE ATTENDING</span>
                  <div className="flex-1" />
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${event.status === 'COLLECTING' ? 'bg-[#E4F4EC] text-[#1F8A55]' : 'bg-[#EFF5FF] text-[#146BFF]'}`}>
                    {event.status === 'COLLECTING' ? 'Active' : event.status}
                  </span>
                </div>
                <h2 className="text-xl font-extrabold text-[#0B1B46] mb-2" data-testid={`contrib-name-${event.id}`}>{event.name}</h2>
                <div className="flex flex-col gap-1 text-sm text-[#5B6472]">
                  <span data-testid={`contrib-date-${event.id}`}>{event.start_at ? new Date(event.start_at).toLocaleDateString() : ''}</span>
                  <span data-testid={`contrib-contributors-${event.id}`}>{event.contributor_count ?? 0} {(event.contributor_count ?? 0) === 1 ? 'contributor' : 'contributors'}</span>
                </div>
                <div className="mt-3 bg-[#EFF5FF] border border-[#D6E4FF] rounded-lg p-3 flex items-start gap-2" data-testid={`contrib-privacy-banner-${event.id}`}>
                  <Lock className="w-4 h-4 text-[#146BFF] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#5B6472]">Your uploads are private. Only the host decides what becomes public.</p>
                </div>
              </div>

              {/* Access status card */}
              {!membership.canUpload && (
                <div className={`border rounded-xl p-4 ${membership.status === 'REMOVED' || membership.status === 'SUSPENDED' ? 'bg-[#FBE5E5] border-[#C93434]/20' : 'bg-[#FBEEDA] border-[#B86500]/20'}`} data-testid={`contrib-access-${event.id}`}>
                  <p className="font-bold text-[#0B1B46]">
                    {membership.status === 'HOST_PENDING' ? 'Awaiting host approval' : membership.status === 'SUSPENDED' ? 'Uploads suspended' : membership.status === 'REMOVED' ? "You've been removed" : 'Verify attendance to unlock uploads'}
                  </p>
                  <p className="text-sm text-[#5B6472] mt-1">
                    {membership.status === 'HOST_PENDING' ? "The host has been notified. You'll see the action tiles below once they approve."
                      : membership.status === 'SUSPENDED' || membership.status === 'REMOVED' ? 'Contact the host if you think this is a mistake.'
                      : "We check that you're at the event before enabling uploads."}
                  </p>
                  {membership.status === 'HOST_PENDING' ? (
                    <button onClick={() => navigate(`/host-approval-required?event_id=${event.id}`)}
                      className="w-full h-10 mt-3 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
                      data-testid={`contrib-approval-btn-${event.id}`}>View approval status</button>
                  ) : membership.status !== 'SUSPENDED' && membership.status !== 'REMOVED' ? (
                    <button onClick={() => navigate(`/verify-attendance?event_id=${event.id}`)}
                      className="w-full h-10 mt-3 bg-[#146BFF] text-white font-semibold rounded-lg hover:bg-[#0F55CC] transition-colors"
                      data-testid={`contrib-verify-btn-${event.id}`}>Verify My Attendance</button>
                  ) : null}
                </div>
              )}

              {/* Lookbook state card */}
              {event.lookbook && (
                event.lookbook.state === 'LIVE' ? (
                  <div className="bg-[#E4F4EC] border border-[#1F8A55]/20 rounded-xl p-4" data-testid={`contrib-lookbook-live-${event.id}`}>
                    <div className="flex items-center gap-2 mb-1"><BookOpen className="w-4 h-4 text-[#1F8A55]" /><span className="font-bold text-[#0B1B46]">Lookbook is live</span></div>
                    <p className="text-sm text-[#5B6472]" data-testid={`contrib-lookbook-live-title-${event.id}`}>{event.lookbook.title || 'Untitled'} · {event.lookbook.photo_count || 0} photos, {event.lookbook.video_count || 0} videos</p>
                  </div>
                ) : event.lookbook.state === 'PAUSED' ? (
                  <div className="bg-[#FBEEDA] border border-[#B86500]/20 rounded-xl p-4" data-testid={`contrib-lookbook-paused-${event.id}`}>
                    <p className="font-bold text-[#0B1B46]">The host paused the lookbook</p>
                    <p className="text-sm text-[#5B6472] mt-1">Check back soon.</p>
                  </div>
                ) : (
                  <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid={`contrib-lookbook-collecting-${event.id}`}>
                    <div className="flex items-center gap-2"><Feather className="w-4 h-4 text-[#146BFF]" /><span className="font-bold text-[#0B1B46]">The Host is collecting and curating memories</span></div>
                    <p className="text-sm text-[#5B6472] mt-1">Once curation is complete you'll see the final Lookbook here.</p>
                  </div>
                )
              )}

              {/* Action tiles */}
              <div className="grid grid-cols-3 gap-3" data-testid={`contrib-action-tiles-${event.id}`}>
                {[
                  { label: 'Take Photos', icon: Camera, mode: 'camera', testId: `contrib-tile-camera-${event.id}` },
                  { label: 'Choose from Phone', icon: Image, mode: 'library', testId: `contrib-tile-library-${event.id}` },
                ].map(({ label, icon: Icon, mode, testId }) => (
                  <button key={mode} onClick={() => handleUploadTilePress(navigate, event.id, membership.status, membership.canUpload, mode)}
                    className={`flex flex-col items-start gap-2 p-3 rounded-xl border min-h-[100px] text-left transition-all hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[#146BFF] ${!membership.canUpload ? 'bg-[#F3F5F8] border-dashed border-[#B86500]' : 'bg-white border-[#E3E8EF] hover:border-[#146BFF]'}`}
                    data-testid={testId}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center relative ${!membership.canUpload ? 'bg-white' : 'bg-[#EFF5FF]'}`}>
                      <Icon className={`w-5 h-5 ${!membership.canUpload ? 'text-[#5B6472]' : 'text-[#146BFF]'}`} />
                      {!membership.canUpload && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#B86500] flex items-center justify-center" data-testid={`${testId}-lock`}>
                          <Lock className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                    </div>
                    <span className={`text-xs font-bold ${!membership.canUpload ? 'text-[#5B6472]' : 'text-[#0B1B46]'}`}>{label}</span>
                    {!membership.canUpload && <span className="text-[10px] font-bold text-[#B86500]" data-testid={`${testId}-locked-reason`}>{lockedReasonFor(membership.status)}</span>}
                  </button>
                ))}
                <button onClick={() => navigate(`/my-views?event_id=${event.id}`)}
                  className="flex flex-col items-start gap-2 p-3 rounded-xl border border-[#E3E8EF] bg-white min-h-[100px] text-left hover:border-[#146BFF] hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
                  data-testid={`contrib-tile-my-views-${event.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#EFF5FF] flex items-center justify-center"><Grid3X3 className="w-5 h-5 text-[#146BFF]" /></div>
                  <span className="text-xs font-bold text-[#0B1B46]">My Views</span>
                </button>
              </div>

              {/* Event details */}
              <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid={`contrib-event-details-${event.id}`}>
                <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest mb-3 uppercase">Event details</p>
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-3 py-2 border-b border-[#E3E8EF]">
                    <div className="w-7 h-7 rounded-md bg-[#EFF5FF] flex items-center justify-center mt-0.5"><MapPin className="w-3.5 h-3.5 text-[#146BFF]" /></div>
                    <div><p className="text-[10px] font-bold text-[#5B6472] tracking-wider">VENUE</p><p className="text-sm font-semibold text-[#0B1B46]">{event.venue_name || '—'}</p>{event.venue_address && <p className="text-xs text-[#5B6472]">{event.venue_address}</p>}</div>
                  </div>
                  <div className="flex items-start gap-3 py-2 border-b border-[#E3E8EF]">
                    <div className="w-7 h-7 rounded-md bg-[#EFF5FF] flex items-center justify-center mt-0.5"><Clock className="w-3.5 h-3.5 text-[#146BFF]" /></div>
                    <div><p className="text-[10px] font-bold text-[#5B6472] tracking-wider">WHEN</p><p className="text-sm font-semibold text-[#0B1B46]">{event.start_at ? new Date(event.start_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</p></div>
                  </div>
                  <div className="flex items-start gap-3 py-2">
                    <div className="w-7 h-7 rounded-md bg-[#EFF5FF] flex items-center justify-center mt-0.5"><CheckCircle className="w-3.5 h-3.5 text-[#146BFF]" /></div>
                    <div><p className="text-[10px] font-bold text-[#5B6472] tracking-wider">MEMBERSHIP</p><p className="text-sm font-semibold text-[#0B1B46]" data-testid={`contrib-membership-status-${event.id}`}>{membership.status}</p></div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button onClick={() => navigate('/join')} className="flex items-center justify-center gap-2 py-3 text-[#146BFF] font-bold hover:underline" data-testid="contrib-join-another">
            <Plus className="w-4 h-4" /> Join another event
          </button>
        </div>
      )}
    </div>
  );
}
